import { storage } from '../api'

/**
 * Video quality: the original stream, the server's 720p transcode
 * ("compressed"), or auto: the original until this viewer's connection
 * shows it cannot keep up.
 */
export type Quality = 'auto' | 'original' | 'compressed'

const KEY = 'chillio.quality'

export function loadQuality(): Quality {
  const q = storage.get(KEY)
  return q === 'original' || q === 'compressed' ? q : 'auto'
}

export function saveQuality(q: Quality) {
  storage.set(KEY, q)
}

/** The transcode's playlists live under v/t720/. */
export function isCompressedLevel(url: string | undefined) {
  return !!url && url.includes('/v/t720/')
}

// When the room waits for this viewer's own connection this long at once,
// or this often, the original is too much for it.
const LONG_WAIT = 8_000
const REPEATED_WAITS = 3
const WAIT_WINDOW = 3 * 60_000
// Throughput below this share of the original's bitrate, over this many
// segments in a row, cannot keep playback going.
const SLOW_SHARE = 0.9
const SLOW_SEGMENTS = 4
const MIN_SEGMENT = 256 << 10

export type DowngradeReason = 'long-wait' | 'repeated-waits' | 'bandwidth'

/**
 * Decides when "auto" drops to the transcode. Waits caused by the server
 * still downloading the torrent are not this viewer's fault and do not
 * count; neither does time the server took before sending a segment.
 */
export class AutoQuality {
  private blamedSince = 0
  private waits: number[] = []
  private slow = 0
  private done = false

  constructor(private readonly onDowngrade: (reason: DowngradeReason) => void) {}

  /** Called regularly with whether the room waits for this viewer's connection. */
  blamed(on: boolean, now: number) {
    if (!on) {
      this.blamedSince = 0
      return
    }
    if (!this.blamedSince) {
      this.blamedSince = now
      this.waits = [...this.waits.filter((at) => now - at < WAIT_WINDOW), now]
      if (this.waits.length >= REPEATED_WAITS) this.down('repeated-waits')
    } else if (now - this.blamedSince >= LONG_WAIT) {
      this.down('long-wait')
    }
  }

  /**
   * A video segment arrived: bytes over the time from its first byte to
   * its last, against the original's bitrate (bits per second).
   */
  segment(bytes: number, transferMs: number, bitrate: number) {
    if (bytes < MIN_SEGMENT || transferMs <= 0 || bitrate <= 0) return
    const throughput = (bytes * 8 * 1000) / transferMs
    this.slow = throughput < bitrate * SLOW_SHARE ? this.slow + 1 : 0
    if (this.slow >= SLOW_SEGMENTS) this.down('bandwidth')
  }

  private down(reason: DowngradeReason) {
    if (this.done) return
    this.done = true
    this.onDowngrade(reason)
  }
}
